/**
 * Sentence plumbing shared by the essay-argument generators.
 *
 * Every generator in this family slots a student's own words into sentence frames, and
 * the words arrive however people type them: with or without a final period,
 * capitalised as if they opened a sentence, occasionally as two sentences. A frame
 * needs them in one of two shapes — a *clause* that sits inside someone else's sentence
 * ("Some readers will object that {opposing}.") or a *sentence* that stands alone — and
 * these helpers produce those shapes without rewriting anything in between.
 *
 * The one judgement call is the first letter. A field moved mid-sentence has its first
 * letter lowercased ("Schools should…" → "…that schools should…"), because that is right
 * for the great majority of what people type. The exceptions are the things a lowercase
 * letter would visibly break: "I", acronyms and brand-style words (NASA, iPhone,
 * COVID-19), a run of capitalised words that reads as a name ("New Deal", "Martin Luther
 * King"), a possessive name ("Orwell's"), and any word the student capitalises somewhere
 * else in their own text — which is how "Seattle" survives at the start of a field.
 *
 * All of that is English-shaped, so every helper takes the page locale (default "en",
 * which leaves English output exactly as it was). Lowercasing happens only where a
 * clause-initial capital is nothing but sentence-initial (`lowersMidSentence`), lists go
 * through `Intl.ListFormat` outside English, and Japanese and Chinese sentences join
 * with no space after "。".
 */

import { getLocaleMeta } from "@/lib/i18n/locales";

const RE_SPACE_RUN = /\s+/g;
/** Soft endings include the full-width and Arabic commas and colons. */
const RE_TRAILING_SOFT = /[\s,;:，、；：،؛]+$/;
/** Any ending mark, including the CJK, Arabic and Devanagari sentence terminators. */
const RE_TRAILING_ANY = /[\s,;:.!?…，、；：،؛。！？｡؟।॥]+$/;
/** "a.m.", "U.S.", "e.g.", "et al.", "Jr." — a final period that belongs to the word. */
const RE_END_ABBREVIATION =
  /(?:\b[A-Za-z]\.){2,}$|\b(?:etc|al|Jr|Sr|Inc|Ltd|Co|vs|Dr|Mr|Mrs|Ms|Prof|St|No|Vol|ed|eds|trans|approx)\.$/;
/** A period or comma just inside a closing quotation mark: `…the rule.”` */
const RE_SOFT_INSIDE_QUOTE = /[.,]+(["”]+)$/;
const RE_HAS_TERMINAL = /[.?!…。！？｡؟।॥]["'”’\]」』]*$/;
const RE_ENDS_SENTENCE = /[.?!。！？؟।]["”’」』]?\s*$/;
const RE_CLOSING_DOUBLE_QUOTE = /["”]+$/;
const RE_FIRST_LETTER = /^([\s"'“‘„«([]*)(\p{L})(\p{L}?)/u;
const RE_FIRST_WORD = /^([\s"'“‘„«([]*)(\p{L}[\p{L}\p{N}'’.-]*)/u;
const RE_INNER_CAPITAL_OR_DIGIT = /[\p{Lu}\p{N}]/u;
const RE_I_CONTRACTION = /^I['’]/;
const RE_POSSESSIVE = /['’]s?$/;
const RE_NEXT_CAPITALISED = /^\s+\p{Lu}\p{Ll}/u;
const RE_DOUBLE_PERIOD = /(?<!\.)\.\.(?!\.)/g;
/** A sentence-ending mark inside a closing quotation mark, then a frame's period. */
const RE_QUOTE_THEN_PERIOD = /([.?!])(["”’]+)\.(?=\s|$)/g;
const RE_SPACE_BEFORE_PUNCT = / +([,.;:!?])/g;
const RE_COMMA_PERIOD = /,\./g;
const RE_WORD = /\p{L}[\p{L}'’-]*/gu;
const RE_CAPITALISED = /^\p{Lu}\p{Ll}/u;
const RE_SENTENCE_START_BEFORE = /(?:^|[.?!:]["”’)]*\s+|\n\s*)["“‘(]*$/;
const RE_POSSESSIVE_SUFFIX = /['’]s$/;
const RE_LEADING_WORD = /^[\s"'“‘(]*(\p{L}[\p{L}'’-]*)/u;
/** "Students", "Schools" — but not "Congress", "Jesus" or "Paris". */
const RE_PLURAL_ENDING = /[^siu]s$/;
const RE_PLACEHOLDER = /\{(\w+)\}/g;
const RE_SENTENCE_BOUNDARY_BEFORE = /(?:^|[.?!]["”’)]*\s+)$/;
const RE_BULLET = /^\s*(?:[-–—•*]|\d+[.)])\s+/;
const RE_A_SOUND = /^(?:eu|one\b|once\b|uni(?![nm]))/i;
const RE_AN_SOUND =
  /^(?:[aeio]|u(?!ni(?![nm])|se|su|ti|to|ra|re|ri|bi)|hono|honest|hour|heir)/i;

/**
 * Function words that never mark a name, even capitalised mid-sentence — "The Great
 * Gatsby" must not teach the helper that "The" is a proper noun.
 */
const STOP_WORDS: ReadonlySet<string> = new Set(
  (
    "a an the this that these those it its they their them he she his her we our you " +
    "your i in on at by for from of to with without into onto over under about after " +
    "before during and but or nor so yet if when while although though because since as " +
    "unless until many most some all each every any no not one more less few several " +
    "such there here what which who whom whose why how where is are was were be been " +
    "being do does did has have had can could should would may might must will shall " +
    "also however therefore thus both either neither other another than then"
  ).split(" ")
);

/** Common nouns that open a sentence in the possessive: "Society's", "Today's". */
const COMMON_POSSESSIVES: ReadonlySet<string> = new Set([
  "society",
  "today",
  "children",
  "women",
  "men",
  "people",
  "everyone",
  "someone",
  "nobody",
  "parents",
  "students",
  "teachers",
  "humanity",
  "nature",
  "history",
  "science",
  "government",
  "school",
  "city",
  "country",
]);

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Languages where a capital letter at the start of a clause is there only because the
 * clause began a sentence, so it can be lowered when the clause moves mid-sentence. The
 * other cased languages the site publishes are left out on purpose: German capitalises
 * every noun ("Die Schule" stays "Die Schule" mid-sentence), and lowering it would be
 * wrong far more often than right. The uncased scripts (Japanese, Chinese, Korean,
 * Arabic, Hindi) need no entry — there is no letter case to change.
 */
const LOWERCASING_LANGUAGES: ReadonlySet<string> = new Set([
  "en",
  "es",
  "fr",
  "it",
  "pt",
  "nl",
  "pl",
  "ru",
  "tr",
  "id",
  "vi",
]);

/** Languages whose sentences run together with no space after the full stop. */
const UNSPACED_LANGUAGES: ReadonlySet<string> = new Set(["ja", "zh"]);

/** The list separator for languages whose comma is not ", ". */
const LIST_SEPARATORS: Readonly<Record<string, string>> = {
  ja: "、",
  zh: "、",
  ar: "، ",
};

/** The language subtag of a locale code or tag: "en-GB" → "en", "zh-Hans" → "zh". */
function languageOf(locale: string): string {
  return locale.split("-")[0].toLowerCase();
}

/** Whether a field's first letter is lowered when it moves into the middle of a sentence. */
export function lowersMidSentence(locale = "en"): boolean {
  return LOWERCASING_LANGUAGES.has(languageOf(locale));
}

/** Upper-case with the language's own rules (Turkish "i" → "İ"); plain on a bad tag. */
function upperCase(text: string, locale: string): string {
  try {
    return text.toLocaleUpperCase(languageOf(locale));
  } catch {
    return text.toUpperCase();
  }
}

/** Lower-case with the language's own rules (Turkish "I" → "ı"); plain on a bad tag. */
function lowerCase(text: string, locale: string): string {
  try {
    return text.toLocaleLowerCase(languageOf(locale));
  } catch {
    return text.toLowerCase();
  }
}

/**
 * What sits between two sentences of one paragraph: a space, except in Japanese and
 * Chinese, where "。" is followed directly by the next sentence.
 */
export function sentenceSeparator(locale = "en"): string {
  return UNSPACED_LANGUAGES.has(languageOf(locale)) ? "" : " ";
}

/** Sentences as one paragraph, spaced the way the language is written. */
export function joinSentences(
  sentences: readonly string[],
  locale = "en"
): string {
  return sentences.join(sentenceSeparator(locale));
}

/** Trim and collapse every run of whitespace — newlines included — to one space. */
export function tidy(text: string): string {
  return text.replace(RE_SPACE_RUN, " ").trim();
}

/**
 * Remove the punctuation a field ends with, so it can sit inside a frame. A period that
 * belongs to an abbreviation ("8:30 a.m.") is kept; a period just inside a closing
 * quotation mark is dropped, since the frame decides how the sentence ends.
 */
export function stripEnd(text: string): string {
  const soft = tidy(text)
    .replace(RE_TRAILING_SOFT, "")
    .replace(RE_SOFT_INSIDE_QUOTE, "$1");
  if (RE_END_ABBREVIATION.test(soft)) {
    return soft;
  }
  return soft.replace(RE_TRAILING_ANY, "");
}

/** "(This was rare.)" ends a sentence; "(Smith et al.)" closes a citation that needs one. */
function isStandaloneParenthetical(text: string): boolean {
  const open = text.lastIndexOf("(");
  if (open === -1) {
    return false;
  }
  const before = text.slice(0, open);
  const opensSentence = before.trim() === "" || RE_ENDS_SENTENCE.test(before);
  return opensSentence && RE_HAS_TERMINAL.test(text.slice(0, -1));
}

/**
 * Make sure a sentence ends with terminal punctuation.
 *
 * A question or exclamation keeps its mark and a trailing ellipsis counts as an ending.
 * A closing parenthesis takes the period after it — "(Smith, 2020)." — unless the
 * parentheses hold a whole sentence of their own. A closing double quotation mark takes
 * the period inside, the American convention APA, MLA and Chicago share:
 * `called it “a disaster.”`
 */
export function ensureTerminal(text: string): string {
  const trimmed = tidy(text).replace(RE_TRAILING_SOFT, "");
  if (trimmed === "") {
    return trimmed;
  }
  if (trimmed.endsWith(")")) {
    return isStandaloneParenthetical(trimmed) ? trimmed : `${trimmed}.`;
  }
  if (RE_HAS_TERMINAL.test(trimmed)) {
    return trimmed;
  }
  const quotes = RE_CLOSING_DOUBLE_QUOTE.exec(trimmed);
  if (quotes) {
    return `${trimmed.slice(0, quotes.index)}.${quotes[0]}`;
  }
  return `${trimmed}.`;
}

/** Uppercase the first letter, skipping leading quotes and brackets; "iPhone" stays. */
export function capitalizeFirst(text: string, locale = "en"): string {
  const match = RE_FIRST_LETTER.exec(text);
  if (!match) {
    return text;
  }
  const [, lead, first, second] = match;
  if (first === upperCase(first, locale)) {
    return text;
  }
  if (second && second !== lowerCase(second, locale)) {
    return text;
  }
  return `${lead}${upperCase(first, locale)}${text.slice(lead.length + 1)}`;
}

function keepsCapital(
  word: string,
  rest: string,
  proper: ReadonlySet<string>,
  english: boolean
): boolean {
  // "I" is a pronoun only in English; in Italian "I ragazzi" is an article.
  if (english && (word === "I" || RE_I_CONTRACTION.test(word))) {
    return true;
  }
  if (RE_INNER_CAPITAL_OR_DIGIT.test(word.slice(1))) {
    return true;
  }
  const bare = word.replace(RE_POSSESSIVE, "").replace(RE_TRAILING_ANY, "");
  if (proper.has(bare)) {
    return true;
  }
  const lower = bare.toLowerCase();
  if (STOP_WORDS.has(lower)) {
    return false;
  }
  if (word !== bare && RE_POSSESSIVE.test(word)) {
    return !COMMON_POSSESSIVES.has(lower);
  }
  return RE_NEXT_CAPITALISED.test(rest);
}

/**
 * Lowercase the first letter of a field that is moving mid-sentence — unless the first
 * word reads as a name. `proper` is the set from `properWords()` over the student's text.
 *
 * Only for languages where that capital is sentence-initial and nothing more
 * (`lowersMidSentence`): in German the field is returned as typed, because "Die Schule"
 * is capitalised wherever it stands.
 */
export function lowerFirst(
  text: string,
  proper: ReadonlySet<string> = EMPTY,
  locale = "en"
): string {
  if (!lowersMidSentence(locale)) {
    return text;
  }
  const match = RE_FIRST_WORD.exec(text);
  if (!match) {
    return text;
  }
  const [, lead, word] = match;
  const first = word.charAt(0);
  if (first === lowerCase(first, locale)) {
    return text;
  }
  const english = languageOf(locale) === "en";
  if (
    keepsCapital(word, text.slice(lead.length + word.length), proper, english)
  ) {
    return text;
  }
  return `${lead}${lowerCase(first, locale)}${text.slice(lead.length + 1)}`;
}

/**
 * Capitalised words that open two or more fields — "Harry was born in Bermuda" and
 * "Harry is a British subject". A name repeats like that; a plural common noun
 * ("Students…", "Students…") can too, so a word ending in a plural "s" is left out.
 */
function repeatedOpeners(texts: readonly string[]): Set<string> {
  const counts = new Map<string, number>();
  for (const text of texts) {
    const word = RE_LEADING_WORD.exec(text)?.[1];
    if (!(word && RE_CAPITALISED.test(word))) {
      continue;
    }
    const bare = word.replace(RE_POSSESSIVE_SUFFIX, "");
    if (STOP_WORDS.has(bare.toLowerCase()) || RE_PLURAL_ENDING.test(bare)) {
      continue;
    }
    counts.set(bare, (counts.get(bare) ?? 0) + 1);
  }
  return new Set(
    [...counts].filter(([, count]) => count >= 2).map(([word]) => word)
  );
}

/**
 * The capitalised words a writer uses *inside* sentences — names, places, titles —
 * plus any that open more than one field. Function words are skipped so a title's
 * "The" never qualifies.
 */
export function properWords(texts: readonly string[]): Set<string> {
  const found = repeatedOpeners(texts);
  for (const text of texts) {
    for (const match of text.matchAll(RE_WORD)) {
      const word = match[0];
      if (!RE_CAPITALISED.test(word)) {
        continue;
      }
      if (RE_SENTENCE_START_BEFORE.test(text.slice(0, match.index))) {
        continue;
      }
      const bare = word.replace(RE_POSSESSIVE_SUFFIX, "");
      if (STOP_WORDS.has(bare.toLowerCase())) {
        continue;
      }
      found.add(bare);
    }
  }
  return found;
}

/** A field shaped to sit inside a frame: no final punctuation, first letter lowered. */
export function asClause(
  text: string,
  proper: ReadonlySet<string> = EMPTY,
  locale = "en"
): string {
  return lowerFirst(stripEnd(text), proper, locale);
}

/** A field shaped to stand alone: capitalised and punctuated. */
export function asSentence(text: string): string {
  return ensureTerminal(capitalizeFirst(tidy(text)));
}

/**
 * Repair the seams a frame leaves: "a.m.." where an abbreviation meets the frame's own
 * period, `.”.` where a quoted sentence does, a space before punctuation, a comma
 * before a period.
 */
export function tidySeams(text: string): string {
  return text
    .replace(RE_DOUBLE_PERIOD, ".")
    .replace(RE_QUOTE_THEN_PERIOD, "$1$2")
    .replace(RE_COMMA_PERIOD, ".")
    .replace(RE_SPACE_BEFORE_PUNCT, "$1")
    .replace(RE_SPACE_RUN, " ")
    .trim();
}

/**
 * Fill a sentence frame. Values arrive clause-shaped; one that opens a sentence — at the
 * start of the frame or after a sentence inside it — is capitalised.
 *
 * Returns null when the frame names a key whose value is null: an optional field left
 * empty drops the sentence that needs it rather than printing half a sentence. A key the
 * values do not mention is left in the text as written.
 */
export function fillFrame(
  template: string,
  values: Readonly<Record<string, string | null>>,
  locale = "en"
): string | null {
  let out = "";
  let last = 0;
  for (const match of template.matchAll(RE_PLACEHOLDER)) {
    const key = match[1];
    if (!(key in values)) {
      continue;
    }
    const value = values[key];
    if (value === null) {
      return null;
    }
    out += template.slice(last, match.index);
    out += RE_SENTENCE_BOUNDARY_BEFORE.test(out)
      ? capitalizeFirst(value, locale)
      : value;
    last = match.index + match[0].length;
  }
  out += template.slice(last);
  const filled = tidySeams(out);
  return filled === "" ? null : ensureTerminal(capitalizeFirst(filled, locale));
}

/**
 * "A, B, と C" is what an English-shaped join makes of a Japanese list. In any language
 * but English the list goes through `Intl.ListFormat`, which knows the separator ("、"
 * in Japanese and Chinese, "، " in Arabic), where the conjunction goes, and whether a
 * serial comma belongs; if the runtime has no data for the locale the items are joined
 * with that language's separator and no conjunction.
 */
function joinLocalised(list: readonly string[], locale: string): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? locale;
  try {
    return new Intl.ListFormat(tag, {
      style: "long",
      type: "conjunction",
    }).format(list);
  } catch {
    return list.join(LIST_SEPARATORS[languageOf(locale)] ?? ", ");
  }
}

/**
 * "a", "a and b", "a, b, and c" — the serial comma APA, MLA and Chicago all use. The
 * `conjunction` is the English word; another `locale` hands the job to `Intl.ListFormat`
 * and ignores it.
 */
export function joinSeries(
  items: readonly string[],
  conjunction = "and",
  locale = "en"
): string {
  const list = items.filter((item) => item !== "");
  if (list.length > 1 && languageOf(locale) !== "en") {
    return joinLocalised(list, locale);
  }
  if (list.length <= 1) {
    return list.join("");
  }
  if (list.length === 2) {
    return `${list[0]} ${conjunction} ${list[1]}`;
  }
  return `${list.slice(0, -1).join(", ")}, ${conjunction} ${list.at(-1)}`;
}

/** "a" or "an" for the word that follows — by sound, for the common English cases. */
export function indefiniteArticle(phrase: string): "a" | "an" {
  const word = phrase.trim();
  if (RE_A_SOUND.test(word)) {
    return "a";
  }
  return RE_AN_SOUND.test(word) ? "an" : "a";
}

/** One item per line, with any bullet or list number the writer typed removed. */
export function splitLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => tidy(line.replace(RE_BULLET, "")))
    .filter((line) => line !== "");
}
